import React, { useEffect, useState } from 'react';
import Row from 'react-bootstrap/Row';
import Col from 'react-bootstrap/Col';
import filter from 'lodash/filter';
import find from 'lodash/find';
import reject from 'lodash/reject';

import { FormSubtitle } from '../styled';
import PhoneField from './PhoneField';
import { UAppPhone } from '../../models/AugmentedClasses';
import { useContactsContext } from '../../contexts/ContactsContext';
import { OtherPlus } from './ContactForm';

import { TAppContact, TAppPhone } from 'TProtocol/common/models';

interface Props {
  contact: TAppContact;
  onChange: (contact: TAppContact) => void;
  isSingle?: boolean;
  autocomplete?: boolean;
}

export enum IUPhoneLabel {
  PrimaryMobile = 1,
  PersonalMobile,
  PersonalLandline,
  ProfessionalMobile,
  ProfessionalLandline,
  OtherPhone
}

export const PhoneLabels: {
  [key in IUPhoneLabel]: string;
} = {
  [IUPhoneLabel.PrimaryMobile]: 'Primary mobile',
  [IUPhoneLabel.PersonalMobile]: 'Personal mobile',
  [IUPhoneLabel.PersonalLandline]: 'Personal landline',
  [IUPhoneLabel.ProfessionalMobile]: 'Professional mobile',
  [IUPhoneLabel.ProfessionalLandline]: 'Professional landline',
  [IUPhoneLabel.OtherPhone]: 'Other phone'
};

export interface PhoneObject {
  phone: UAppPhone;
  label: IUPhoneLabel;
  disabled?: boolean;
}

const PhoneFields = ({ contact, onChange, isSingle = false, autocomplete = false }: Props) => {
  const contactsContext = useContactsContext();
  const [phones, setPhones] = useState<PhoneObject[]>([]);

  useEffect(() => {
    const phones: PhoneObject[] = [];
    if (contact.primaryMobile) {
      phones.push({
        phone: contact.primaryMobile,
        label: IUPhoneLabel.PrimaryMobile,
        disabled: contact.contactId === contactsContext.selfContact?.contactId
      });
    }
    if (contact.personalMobile) {
      phones.push({
        phone: contact.personalMobile,
        label: IUPhoneLabel.PersonalMobile
      });
    }
    if (contact.personalLandline) {
      phones.push({
        phone: contact.personalLandline,
        label: IUPhoneLabel.PersonalLandline
      });
    }
    if (contact.professionalMobile) {
      phones.push({
        phone: contact.professionalMobile,
        label: IUPhoneLabel.ProfessionalMobile
      });
    }
    if (contact.professionalLandline) {
      phones.push({
        phone: contact.professionalLandline,
        label: IUPhoneLabel.ProfessionalLandline
      });
    }
    if (contact.otherPhones) {
      contact.otherPhones.forEach((phone) => {
        phones.push({
          phone,
          label: IUPhoneLabel.OtherPhone
        });
      });
    }

    setPhones(phones);
  }, []);

  const handleDelete = (phone: PhoneObject) => {
    removePhone(phone);
    setPhones(reject(phones, phone));
    onChange(contact);
  };

  const handleChange = (phone: PhoneObject, value: string) => {
    phone.phone.phoneNumber = value;

    if (phone.label === IUPhoneLabel.PrimaryMobile && contact.primaryMobile) {
      contact.primaryMobile.phoneNumber = value;
      contact.primaryMobile.displayNumber = value;
    } else if (phone.label === IUPhoneLabel.PersonalMobile && contact.personalMobile) {
      contact.personalMobile.phoneNumber = value;
      contact.personalMobile.displayNumber = value;
    } else if (phone.label === IUPhoneLabel.PersonalLandline && contact.personalLandline) {
      contact.personalLandline.phoneNumber = value;
      contact.personalLandline.displayNumber = value;
    } else if (phone.label === IUPhoneLabel.ProfessionalMobile && contact.professionalMobile) {
      contact.professionalMobile.phoneNumber = value;
      contact.professionalMobile.displayNumber = value;
    } else if (phone.label === IUPhoneLabel.ProfessionalLandline && contact.professionalLandline) {
      contact.professionalLandline.phoneNumber = value;
      contact.professionalLandline.displayNumber = value;
    } else if (phone.label === IUPhoneLabel.OtherPhone) {
      const otherPhone = find<UAppPhone>(contact.otherPhones, { _key: phone.phone._key });
      if (otherPhone) {
        otherPhone.phoneNumber = value;
        otherPhone.displayNumber = value;
      }
    }

    onChange(contact);
  };

  const handleLabelChange = (phone: PhoneObject, previousLabel: IUPhoneLabel) => {
    removePhone({
      phone: phone.phone,
      label: previousLabel
    });
    moveExistingPhoneToOther(phone.label);
    updatePhones(phone);
    updatePhoneField(phone.label, phone.phone);
    setPhones([...phones]);
    onChange(contact);
  };

  const addPhone = () => {
    const phone = new UAppPhone();
    let label = IUPhoneLabel.OtherPhone;

    if (!contact.primaryMobile) {
      contact.primaryMobile = phone;
      label = IUPhoneLabel.PrimaryMobile;
    } else if (!contact.personalMobile) {
      contact.personalMobile = phone;
      label = IUPhoneLabel.PersonalMobile;
    } else if (!contact.professionalMobile) {
      contact.professionalMobile = phone;
      label = IUPhoneLabel.ProfessionalMobile;
    } else if (!contact.personalLandline) {
      contact.personalLandline = phone;
      label = IUPhoneLabel.PersonalLandline;
    } else if (!contact.professionalLandline) {
      contact.professionalLandline = phone;
      label = IUPhoneLabel.ProfessionalLandline;
    } else {
      if (!contact.otherPhones) {
        contact.otherPhones = [];
      }
      contact.otherPhones.push(phone);
    }

    phones.push({ phone, label });

    onChange(contact);
  };

  const removePhone = (phone: PhoneObject) => {
    if (phone.label === IUPhoneLabel.OtherPhone) {
      removeOtherPhone(phone.phone);
    } else {
      updatePhoneField(phone.label, undefined);
    }
  };

  const removeOtherPhone = (phone: TAppPhone) => {
    // Have to find the phone by value, as there is no other identifier we can use.
    let found = false;
    contact.otherPhones = filter(contact.otherPhones, (otherPhone) => {
      if (!found && phone.phoneNumber === otherPhone.phoneNumber) {
        found = true;
        return false;
      }
      return true;
    });
  };

  const moveExistingPhoneToOther = (label: IUPhoneLabel) => {
    let existingPhone: TAppPhone | undefined;
    if (label === IUPhoneLabel.PrimaryMobile) {
      existingPhone = contact.primaryMobile;
    } else if (label === IUPhoneLabel.PersonalMobile) {
      existingPhone = contact.personalMobile;
    } else if (label === IUPhoneLabel.PersonalLandline) {
      existingPhone = contact.personalLandline;
    } else if (label === IUPhoneLabel.ProfessionalMobile) {
      existingPhone = contact.professionalMobile;
    } else if (label === IUPhoneLabel.ProfessionalLandline) {
      existingPhone = contact.professionalLandline;
    }

    if (existingPhone !== undefined) {
      if (!contact.otherPhones) {
        contact.otherPhones = [];
      }
      contact.otherPhones.push(existingPhone);
    }

    const existingPhoneObject = find(phones, { label });
    if (existingPhoneObject) {
      existingPhoneObject.label = IUPhoneLabel.OtherPhone;
    }
  };

  const updatePhones = (phone: PhoneObject) => {
    const existingPhone = phones.find((p) => p.phone._key === phone.phone._key);
    if (existingPhone) {
      existingPhone.phone = phone.phone;
      existingPhone.label = phone.label;
    }
  };

  const updatePhoneField = (label: IUPhoneLabel, phone: TAppPhone | undefined) => {
    if (label === IUPhoneLabel.PrimaryMobile) {
      contact.primaryMobile = phone;
    } else if (label === IUPhoneLabel.PersonalMobile) {
      contact.personalMobile = phone;
    } else if (label === IUPhoneLabel.PersonalLandline) {
      contact.personalLandline = phone;
    } else if (label === IUPhoneLabel.ProfessionalMobile) {
      contact.professionalMobile = phone;
    } else if (label === IUPhoneLabel.ProfessionalLandline) {
      contact.professionalLandline = phone;
    } else if (label === IUPhoneLabel.OtherPhone && phone !== undefined) {
      if (!contact.otherPhones) {
        contact.otherPhones = [];
      }
      contact.otherPhones.push(phone);
    }
  };

  return <>
    <FormSubtitle>{isSingle ? 'Phone' : 'Phones'}</FormSubtitle>

    {
      phones.map((phone) => {
        return <Row key={phone.phone._key}>
          <Col>
            <PhoneField
              contact={contact}
              phone={phone}
              hideLabel={isSingle}
              autocomplete={autocomplete}
              handleDelete={() => {
                handleDelete(phone);
              }}
              handleChange={(value) => {
                handleChange(phone, value);
              }}
              handleLabelChange={handleLabelChange}
            />
          </Col>
        </Row>;
      })
    }

    {
      isSingle ? null : <div onClick={addPhone}>
        <OtherPlus>+</OtherPlus> Phone
      </div>
    }
  </>;
};

export default PhoneFields;
